import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { getImageUrl } from '../../utils/imageUrl';

const MenuItems = () => {
  const MySwal = withReactContent(Swal);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  const [formData, setFormData] = useState({
    productName: '',
    category: '',
    price: '',
    preparationTime: 15,
    description: '',
    isAvailable: true
  });
  const [variations, setVariations] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchItems();
    fetchCategories();
  }, []);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await axios.get("https://avastar-pos-backend.onrender.com/api/products");
      setItems(res.data);
    } catch (err) {
      console.error(err);
      MySwal.fire('Error', 'Failed to fetch menu items', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get("https://avastar-pos-backend.onrender.com/api/categories?type=Menu");
      setCategories(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  // â”€â”€ Variation helpers â”€â”€
  const addVariation = () => {
    setVariations([...variations, { name: '', price: '' }]);
  };

  const updateVariation = (index, field, value) => {
    const updated = [...variations];
    updated[index][field] = value;
    setVariations(updated);
  };

  const removeVariation = (index) => {
    setVariations(variations.filter((_, i) => i !== index));
  };

  const openAddModal = () => {
    setEditingItem(null);
    setFormData({
      productName: '',
      category: categories.length > 0 ? categories[0]._id : '',
      price: '',
      preparationTime: 15,
      description: '',
      isAvailable: true
    });
    setVariations([]);
    setSelectedFile(null);
    setImagePreview(null);
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setEditingItem(item._id);
    setFormData({
      productName: item.productName,
      category: item.category?._id || '',
      price: item.price,
      preparationTime: item.preparationTime || 15,
      description: item.description || '',
      isAvailable: item.isAvailable
    });
    setVariations(item.variations && item.variations.length > 0 
      ? item.variations.map(v => ({ name: v.name, price: v.price })) 
      : []
    );
    setSelectedFile(null);
    setImagePreview(getImageUrl(item.image));
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = new FormData();
      data.append('productName', formData.productName);
      data.append('category', formData.category);
      data.append('price', formData.price);
      data.append('preparationTime', formData.preparationTime);
      data.append('description', formData.description);
      data.append('isAvailable', formData.isAvailable);
      // Send variations as JSON string
      const validVariations = variations.filter(v => v.name && v.price);
      data.append('variations', JSON.stringify(validVariations));
      if (selectedFile) data.append('image', selectedFile);

      const config = { headers: { 'Content-Type': 'multipart/form-data' } };

      if (editingItem) {
        await axios.put(`https://avastar-pos-backend.onrender.com/api/products/${editingItem}`, data, config);
        MySwal.fire('Success', 'Item updated successfully', 'success');
      } else {
        await axios.post("https://avastar-pos-backend.onrender.com/api/products", data, config);
        MySwal.fire('Success', 'Item added successfully', 'success');
      }
      setShowModal(false);
      fetchItems();
    } catch (err) {
      console.error(err);
      MySwal.fire('Error', 'Failed to save item', 'error');
    }
  };

  const toggleAvailability = async (id, currentStatus) => {
    try {
      await axios.patch(`https://avastar-pos-backend.onrender.com/api/products/${id}/toggle`);
      setItems(items.map(item => 
        item._id === id ? { ...item, isAvailable: !currentStatus } : item
      ));
    } catch (err) {
      console.error(err);
      MySwal.fire('Error', 'Failed to toggle status', 'error');
    }
  };

  const deleteItem = async (id) => {
    MySwal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(`https://avastar-pos-backend.onrender.com/api/products/${id}`);
          MySwal.fire('Deleted!', 'Menu item has been deleted.', 'success');
          fetchItems();
        } catch (err) {
          console.error(err);
          MySwal.fire('Error', 'Failed to delete item', 'error');
        }
      }
    });
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="page-title">
            <h4>Menu Items Management</h4>
            <h6>Manage your restaurant&apos;s food and beverage items</h6>
          </div>
          <div className="page-btn">
            <button className="btn btn-added" onClick={openAddModal}>
              <i className="fas fa-plus me-2"></i> Add Menu Item
            </button>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            {loading ? (
              <div className="text-center p-5">Loading...</div>
            ) : (
              <div className="table-responsive">
                <table className="table datanew">
                  <thead>
                    <tr>
                      <th style={{ width: '60px' }}>Image</th>
                      <th>Product Name</th>
                      <th>Category</th>
                      <th>Price (LKR)</th>
                      <th>Variations</th>
                      <th>Prep Time (mins)</th>
                      <th>Status (POS)</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map(item => (
                      <tr key={item._id}>
                        <td>
                          {item.image ? (
                            <img src={getImageUrl(item.image)} alt={item.productName} style={{ width: '44px', height: '44px', borderRadius: '10px', objectFit: 'cover' }} />
                          ) : (
                            <div style={{ width: '44px', height: '44px', borderRadius: '10px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
                              <i className="fas fa-image"></i>
                            </div>
                          )}
                        </td>
                        <td className="fw-bold">{item.productName}</td>
                        <td>{item.category?.name || 'Uncategorized'}</td>
                        <td>{item.price.toFixed(2)}</td>
                        <td>
                          {item.variations && item.variations.length > 0 ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                              {item.variations.map((v, i) => (
                                <span key={i} className="badge bg-primary bg-opacity-10 text-primary rounded-pill px-2 py-1" style={{ fontSize: '11px' }}>
                                  {v.name} - Rs {v.price}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-muted" style={{ fontSize: '12px' }}>No variations</span>
                          )}
                        </td>
                        <td>{item.preparationTime}</td>
                        <td>
                          <div className="form-check form-switch">
                            <input 
                              className="form-check-input" 
                              type="checkbox" 
                              id={`flexSwitchCheckChecked-${item._id}`}
                              checked={item.isAvailable}
                              onChange={() => toggleAvailability(item._id, item.isAvailable)}
                              style={{cursor: 'pointer'}}
                            />
                            <label className="form-check-label" htmlFor={`flexSwitchCheckChecked-${item._id}`}>
                              {item.isAvailable ? <span className="text-success fw-bold">Available</span> : <span className="text-danger fw-bold">Out of Stock</span>}
                            </label>
                          </div>
                        </td>
                        <td>
                          <button className="btn btn-sm btn-outline-info me-2" onClick={() => openEditModal(item)}>
                            <i className="fas fa-edit"></i>
                          </button>
                          <button className="btn btn-sm btn-outline-danger" onClick={() => deleteItem(item._id)}>
                            <i className="fas fa-trash"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal for Add/Edit using Bootstrap modal styling */}
      {showModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingItem ? 'Edit Menu Item' : 'Add Menu Item'}</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)} aria-label="Close"></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label className="form-label">Item Name</label>
                    <input type="text" className="form-control" name="productName" value={formData.productName} onChange={handleInputChange} required />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Category</label>
                    <select className="form-select" name="category" value={formData.category} onChange={handleInputChange} required>
                      <option value="" disabled>Select Category</option>
                      {categories.map(cat => (
                        <option key={cat._id} value={cat._id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                        <label className="form-label">Base Price (LKR)</label>
                        <input type="number" step="0.01" className="form-control" name="price" value={formData.price} onChange={handleInputChange} required />
                    </div>
                    <div className="col-md-6 mb-3">
                        <label className="form-label">Prep Time (mins)</label>
                        <input type="number" className="form-control" name="preparationTime" value={formData.preparationTime} onChange={handleInputChange} required />
                    </div>
                  </div>

                  {/* â”€â”€ Variations Section â”€â”€ */}
                  <div className="mb-3">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <label className="form-label mb-0 fw-bold">
                        <i className="fas fa-layer-group me-2 text-primary"></i>Variations / Sizes
                      </label>
                      <button type="button" className="btn btn-sm btn-outline-primary rounded-pill" onClick={addVariation}>
                        <i className="fas fa-plus me-1"></i> Add Variation
                      </button>
                    </div>
                    {variations.length === 0 && (
                      <div className="bg-light border border-secondary rounded p-3 text-center text-muted" style={{ fontSize: '13px', borderStyle: 'dashed !important' }}>
                        <i className="fas fa-info-circle me-1"></i>
                        No variations added. Item will use the base price only.
                      </div>
                    )}
                    {variations.map((v, idx) => (
                      <div key={idx} className="d-flex align-items-center gap-2 mb-2 bg-light border rounded p-2">
                        <span className="text-muted fw-bold" style={{ fontSize: '12px', minWidth: '20px' }}>{idx + 1}.</span>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="e.g. Single Portion, Normal, Full"
                          value={v.name}
                          onChange={(e) => updateVariation(idx, 'name', e.target.value)}
                          style={{ flex: 2 }}
                        />
                        <div className="input-group input-group-sm" style={{ flex: 1 }}>
                          <span className="input-group-text" style={{ fontSize: '11px' }}>Rs</span>
                          <input
                            type="number"
                            step="0.01"
                            className="form-control form-control-sm"
                            placeholder="Price"
                            value={v.price}
                            onChange={(e) => updateVariation(idx, 'price', e.target.value)}
                          />
                        </div>
                        <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeVariation(idx)} style={{ padding: '2px 8px' }}>
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="mb-3">
                    <div className="form-check form-switch mt-2">
                      <input className="form-check-input" type="checkbox" name="isAvailable" checked={formData.isAvailable} onChange={handleInputChange} />
                      <label className="form-check-label">Available in POS</label>
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Item Image</label>
                    <div 
                      className="bg-light border border-secondary rounded text-center p-3"
                      onClick={() => fileInputRef.current?.click()} 
                      style={{ borderStyle: 'dashed !important', cursor: 'pointer' }}
                    >
                      {(imagePreview || selectedFile) ? (
                        <img src={selectedFile ? URL.createObjectURL(selectedFile) : imagePreview} alt="preview" 
                          style={{ maxHeight: '100px', borderRadius: '8px', objectFit: 'cover' }} />
                      ) : (
                        <div>
                          <i className="fas fa-cloud-upload-alt text-primary" style={{ fontSize: '1.5rem' }}></i>
                          <p className="text-muted mb-0 small">Click to upload image</p>
                          <small className="text-muted">Auto-compressed</small>
                        </div>
                      )}
                    </div>
                    <input type="file" ref={fileInputRef} accept="image/*" style={{ display: 'none' }}
                      onChange={(e) => { const f = e.target.files[0]; if (f) { setSelectedFile(f); setImagePreview(null); } }} />
                  </div>
                  <div className="text-end mt-4">
                    <button type="button" className="btn btn-secondary me-2" onClick={() => setShowModal(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary">Save Item</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MenuItems;
